import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { calcFilterPos, calcOpDropdownPos } from "../waterfall-eac/filterPopoverUtils";
import { NumericFilterContent, TextFilterContent, OpDropdownContent, fpBase } from "../waterfall-eac/tableColumnFilterShared";
import {
  toggleSortKey,
  compareBySortKeys,
  createSortThClick,
  WaterfallThLabel,
  fmtPctDisplay,
} from "../waterfall-eac/waterfallTableUtils";
import {
  TABLE_FONT,
  ACCENT_COLOR,
  TH_BG,
  TH_BG_BAND,
  Z_HEAD,
  Z_STICKY,
} from "../../reportTableConfig";

const FONT = TABLE_FONT;
const SC_ACCENT = ACCENT_COLOR;

/* ── Fixed column definitions ── */
export const FIXED_COLS = [
  { key: "id",          label: "Project #",    w: 102, align: "left" },
  { key: "projectName", label: "Project Name", w: 200, align: "left" },
  { key: "pm",          label: "PM",           w: 162, align: "left" },
];
let _left = 0;
FIXED_COLS.forEach((c) => { c.left = _left; _left += c.w; });
export const FIXED_W = _left;

/** Extra width beyond tab `w` — sized for label + sort + filter icons without overlap */
const METRIC_W_PAD = 20;
const METRIC_HEADER_ICONS_W = 44;

function effectiveMetricWidth(col) {
  const flatLabel = col.label.replace(/\n/g, " ");
  const textW = Math.ceil(flatLabel.length * 6.2) + METRIC_HEADER_ICONS_W + METRIC_W_PAD;
  return Math.max(col.w + METRIC_W_PAD, textW);
}

const IND_W = 78;
const GH = 34; const RH = 36;

export const INDICATORS = ["financial","performer","funding","staffing","culture","customer","doge"];
export const IND_LABELS = { financial:"Financial",performer:"Performer",funding:"Funding",staffing:"Staffing",culture:"Culture",customer:"Customer",doge:"Doge" };
export const G = "green", O = "orange", R = "red";

/** Map API status color (hex or token) to StatusDot value. */
export function hexToStatus(hex) {
  if (hex == null || hex === "") return null;
  const h = String(hex).trim().toLowerCase();
  if (h === "#ff0000" || h === "red") return R;
  if (h === "#ffff00" || h === "orange") return O;
  if (h === "green") return G;
  return G;
}

/* ── Formatters ── */
export const fmtCurrency = (v) => {
  if (v === null || v === undefined) return "";
  const n = Number(v);
  const abs = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `($${abs})` : `$${abs}`;
};
export const fmtPlain = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function formatMetricCell(col, v) {
  if (col.isPct) return fmtPctDisplay(v);
  if (col.isCurrency) return fmtCurrency(v);
  return fmtPlain(v);
}

/* ── Status dot ── */
export function StatusDot({ value }) {
  if (value === 0 || value === null || value === undefined)
    return <span style={{ fontSize: 11, color: "#94A3B8" }}>—</span>;
  const bg = value === R ? "#EF4444" : value === O ? "#F59E0B" : "#22C55E";
  return <span style={{ display: "inline-block", width: 13, height: 13, borderRadius: "50%", background: bg, boxShadow: `0 0 0 2px ${bg}33` }} />;
}

/* ── Column filter helpers ── */
const OP_LABELS = { gt: ">", gte: "≥", lt: "<", lte: "≤", eq: "=", neq: "≠" };
const OP_ENTRIES = [
  { key: "gt",  label: "> Greater than"          },
  { key: "gte", label: "≥ Greater than or equal" },
  { key: "lt",  label: "< Less than"             },
  { key: "lte", label: "≤ Less than or equal"    },
  { key: "eq",  label: "= Equals"                },
  { key: "neq", label: "≠ Not equal"             },
];

function applyNumFilter(rawVal, filter) {
  if (!filter || filter.value === "" || filter.value === undefined) return true;
  const v  = rawVal === null || rawVal === undefined ? 0 : Number(rawVal);
  const fv = Number(filter.value);
  switch (filter.op || "gt") {
    case "gt":  return v > fv;
    case "gte": return v >= fv;
    case "lt":  return v < fv;
    case "lte": return v <= fv;
    case "eq":  return v === fv;
    case "neq": return v !== fv;
    default:    return true;
  }
}

/* ── Shared inline style helpers ── */
const TH_SH_BOT_G = "inset 0 -2px 0 rgba(255,255,255,.22)";
const TH_SH_BOT   = "inset 0 -1px 0 rgba(255,255,255,.12)";

const thCore = (z, h, padding, bg) => ({
  position: "sticky", top: 0, zIndex: z, height: h, lineHeight: 1.25,
  boxSizing: "border-box", background: bg, color: "#fff",
  verticalAlign: "middle", padding, whiteSpace: "nowrap", border: "none",
  overflow: "visible",
});

const thFixed = (col, isLast) => ({
  ...thCore(Z_HEAD + 1, GH, "7px 10px", TH_BG),
  left: col.left, width: col.w, minWidth: col.w, maxWidth: col.w,
  fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
  textTransform: "uppercase", textAlign: col.align,
  boxShadow: isLast ? `4px 0 8px -2px rgba(0,0,0,0.12), ${TH_SH_BOT}` : TH_SH_BOT,
});

const thMetric = (w) => ({
  ...thCore(Z_HEAD, GH, "7px 10px", TH_BG),
  fontSize: 10, fontWeight: 600, letterSpacing: "0.04em",
  textAlign: "right", color: "rgba(255,255,255,.75)",
  width: w, minWidth: w, maxWidth: w, whiteSpace: "nowrap",
  boxShadow: TH_SH_BOT,
  overflow: "hidden",
});

const thInd = (isFirst) => ({
  ...thCore(Z_HEAD, GH, "7px 6px", isFirst ? TH_BG_BAND : TH_BG),
  fontSize: 10, fontWeight: 600, letterSpacing: "0.04em",
  textAlign: "center", color: "rgba(255,255,255,.75)",
  width: IND_W, minWidth: IND_W,
  boxShadow: isFirst ? TH_SH_BOT_G : TH_SH_BOT,
});

const tdBase = (bg, align = "right") => ({
  boxSizing: "border-box", background: bg, textAlign: align,
  padding: "7px 10px", fontSize: 12, color: "#334155", whiteSpace: "nowrap",
  overflow: "hidden", verticalAlign: "middle", fontVariantNumeric: "tabular-nums",
  borderBottom: "1px solid #E2E8F0",
});

const tdFixed = (bg, col, isLast) => ({
  ...tdBase(bg, col.align),
  position: "sticky", left: col.left, zIndex: Z_STICKY,
  width: col.w, minWidth: col.w, maxWidth: col.w,
  fontWeight: col.key === "id" ? 600 : 400,
  boxShadow: isLast ? "4px 0 8px -2px rgba(0,0,0,0.06)" : undefined,
});

const tdTot = (col, isLast) => ({
  ...tdFixed("#F0FFF4", col, isLast), color: "#111827", fontWeight: 700,
  borderBottom: "1px solid #CBD5E1",
});
const tdTotScroll = () => ({
  ...tdBase("#F0FFF4"), color: "#111827", fontWeight: 700,
  borderBottom: "1px solid #CBD5E1",
});
const tdTotInd = () => ({
  ...tdBase("#F0FFF4", "center"), color: "#111827",
  borderBottom: "1px solid #CBD5E1",
});

const CARD  = { background: "#fff", borderRadius: 12, border: "1px solid #EBEBEB", boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)", overflow: "visible", display: "flex", flexDirection: "column", marginBottom: 20 };
const HEAD  = { padding: "16px 20px 12px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, position: "relative", zIndex: 20, overflow: "visible", flexShrink: 0, background: "#fff", borderTopLeftRadius: 12, borderTopRightRadius: 12 };
const CARD_BODY = { overflow: "hidden", borderBottomLeftRadius: 12, borderBottomRightRadius: 12 };
const TITLE = { fontSize: 14, fontWeight: 600, color: "#111827", letterSpacing: "-0.01em", fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif', margin: 0 };

/* ── Shared icon components ── */
function FilterFunnelIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

/* ── Shared style tokens for filter UI ── */
const filterIconBtn = (active) => ({
  flexShrink: 0, width: 16, height: 16, border: "none",
  background: "transparent", cursor: "pointer", padding: 0,
  borderRadius: 3, color: active ? "#A5B4FC" : "rgba(255,255,255,0.5)",
  background: active ? "rgba(165,180,252,0.18)" : "transparent",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  verticalAlign: "middle",
});
const filterPanelStyle = {
  position: "fixed", zIndex: 99, background: "#fff",
  border: "1px solid #E2E8F0", borderRadius: 8,
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  padding: "10px 12px", minWidth: 190, fontFamily: FONT,
};
const filterWrap = { display: "flex", alignItems: "center", gap: 3, height: 26 };
const filterTextInput = (focused) => ({
  flex: 1, height: 26, border: `1.5px solid ${focused ? "#4F46E5" : "#CBD5E1"}`,
  borderRadius: 5, fontSize: 11, padding: "0 7px", outline: "none",
  fontFamily: FONT, background: "#fff", color: "#111827",
});
const filterNumInput = {
  flex: 1, height: 26, border: "1.5px solid #CBD5E1", borderRadius: 5,
  fontSize: 11, padding: "0 5px", outline: "none", fontFamily: FONT,
  background: "#fff", color: "#111827", textAlign: "right",
};
const opBtn = (active) => ({
  flexShrink: 0, width: 22, height: 26, border: `1.5px solid ${active ? "#4F46E5" : "#CBD5E1"}`,
  borderRadius: 5, background: active ? "#4F46E5" : "#fff",
  fontSize: 11, fontWeight: 700, color: active ? "#fff" : "#4F46E5",
  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
  padding: 0, fontFamily: FONT,
});
const clearBtn = {
  flexShrink: 0, width: 18, height: 18, border: "none", borderRadius: "50%",
  background: "#E2E8F0", fontSize: 9, color: "#64748B", cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
};
const opDropdownStyle = {
  position: "fixed", zIndex: 101, background: "#fff",
  border: "1px solid #E2E8F0", borderRadius: 8,
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)", minWidth: 190, overflow: "hidden", fontFamily: FONT,
};

const CARD_META = { fontSize: 12, fontWeight: 400, color: "#6B7280", fontFamily: FONT, lineHeight: 1.4, marginTop: 2 };

/* ── Pagination footer styles (matches Waterfall View) ── */
const paginationBar = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "10px 20px", borderTop: "1px solid #E2E8F0",
  background: "#FAFAFA", flexShrink: 0, gap: 12, flexWrap: "wrap",
};
const paginationInfo     = { fontSize: 12, color: "#6B7280", fontFamily: FONT, whiteSpace: "nowrap" };
const paginationRight    = { display: "flex", alignItems: "center", gap: 8 };
const paginationControls = { display: "flex", alignItems: "center", gap: 3 };
const pageSizeLabel      = { fontSize: 12, color: "#6B7280", fontFamily: FONT, whiteSpace: "nowrap" };
const pageSizeSelect = {
  height: 28, border: "1px solid #E2E8F0", borderRadius: 6,
  fontSize: 12, color: "#374151", padding: "0 6px",
  background: "#fff", cursor: "pointer", fontFamily: FONT,
};
const pageBtn = (disabled) => ({
  minWidth: 30, height: 28, padding: "0 6px",
  border: "1px solid #E2E8F0", borderRadius: 6,
  background: "#fff", color: disabled ? "#94A3B8" : "#374151",
  fontSize: 12, fontWeight: 500, cursor: disabled ? "default" : "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  fontFamily: FONT, opacity: disabled ? 0.35 : 1,
});
const pageBtnActive = {
  minWidth: 30, height: 28, padding: "0 6px",
  border: `1px solid ${TH_BG_BAND}`, borderRadius: 6,
  background: TH_BG_BAND, color: "#fff",
  fontSize: 12, fontWeight: 600, cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  fontFamily: FONT,
};
const pageDots = { fontSize: 12, color: "#6B7280", padding: "0 4px", userSelect: "none" };

/* ── Column picker styles ── */
const colsBtn = (active) => ({
  display: "inline-flex", alignItems: "center", gap: 6, height: 30, padding: "0 12px",
  borderRadius: 6, border: `1px solid ${active ? "#C7D2FE" : "#E2E8F0"}`,
  background: active ? "#EEF2FF" : "#fff", fontSize: 12, fontWeight: 500,
  color: active ? "#4F46E5" : "#374151", cursor: "pointer", fontFamily: FONT,
});
const hiddenBadge = {
  display: "inline-flex", alignItems: "center", height: 18, padding: "0 7px",
  borderRadius: 10, background: "#4F46E5", color: "#fff", fontSize: 11, fontWeight: 600,
};
const colPicker = {
  position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 1200,
  background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10,
  boxShadow: "0 8px 28px rgba(0,0,0,0.12)", minWidth: 200, maxHeight: 420,
  overflowY: "auto", fontFamily: FONT,
};
const colPickerHeader = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "10px 14px 8px", borderBottom: "1px solid #F3F4F6",
};
const colPickerItem = {
  display: "flex", alignItems: "center", gap: 9, padding: "7px 14px",
  cursor: "pointer", fontSize: 12, color: "#374151", userSelect: "none",
  borderBottom: "1px solid #F9FAFB",
};

/* ═════════════════════════════════════════════════════════
   HeatmapTable
═════════════════════════════════════════════════════════ */
export default function HeatmapTable({ title, cols, totalRow, rows }) {
  /* ── filter state ── */
  const [textFilters, setTextFilters] = useState({});  // { id: "22402", projectName: "...", pm: "..." }
  const [numFilters,  setNumFilters]  = useState({});  // { colKey: { op, value } }

  /* ── column picker ── */
  const [hiddenCols,    setHiddenCols]    = useState(new Set());
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const colPickerRef = useRef(null);

  /* ── filter panel / op dropdown ── */
  const [openFilterKey,   setOpenFilterKey]   = useState(null);
  const [filterPanelPos,  setFilterPanelPos]  = useState({ top: 0, left: 0 });
  const [openOpKey,       setOpenOpKey]       = useState(null);
  const [opDropdownPos,   setOpDropdownPos]   = useState({ top: 0, left: 0 });
  const filterBtnRefs = useRef({});
  const opBtnRefs     = useRef({});
  const openFilterKeyRef = useRef(null);
  const openOpKeyRef     = useRef(null);

  /* ── pagination ── */
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(10);

  /* ── sorting ── */
  const [sortKeys, setSortKeys] = useState([]);

  useEffect(() => { openFilterKeyRef.current = openFilterKey; }, [openFilterKey]);
  useEffect(() => { openOpKeyRef.current     = openOpKey;     }, [openOpKey]);

  /* Reposition panels on scroll / resize */
  useEffect(() => {
    const reposition = () => {
      const fk = openFilterKeyRef.current;
      if (fk) {
        const btn = filterBtnRefs.current[fk];
        if (btn) setFilterPanelPos(calcFilterPos(btn));
      }
      const ok = openOpKeyRef.current;
      if (ok) {
        const ob = opBtnRefs.current[ok];
        if (ob) setOpDropdownPos(calcOpDropdownPos(ob));
      }
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, []);

  /* Close col picker on outside click */
  useEffect(() => {
    if (!colPickerOpen) return;
    const h = (e) => { if (colPickerRef.current && !colPickerRef.current.contains(e.target)) setColPickerOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [colPickerOpen]);

  /* Close filter panel on outside click */
  useEffect(() => {
    if (!openFilterKey) return;
    const h = (e) => {
      if (!e.target.closest("[data-filter-panel]") && !e.target.closest("[data-filter-btn]") && !e.target.closest("[data-op-dd]") && !e.target.closest("[data-op-dropdown]"))
        setOpenFilterKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey]);

  /* Close op dropdown on outside click */
  useEffect(() => {
    if (!openOpKey) return;
    const h = (e) => {
      if (!e.target.closest("[data-op-dd]") && !e.target.closest("[data-op-dropdown]") && !e.target.closest("[data-op-btn]"))
        setOpenOpKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openOpKey]);

  /* Reset page when filters or sort change */
  useEffect(() => { setPage(1); }, [textFilters, numFilters]);

  /* ── all hideable columns ── */
  const hideableCols = [
    ...FIXED_COLS.filter((c) => c.key !== "projectName").map((c) => ({ key: c.key, label: c.label, group: "fixed" })),
    ...cols.map((c) => ({ key: c.key, label: c.label.replace(/\n/g, " "), group: "metric" })),
    ...INDICATORS.map((k) => ({ key: k, label: IND_LABELS[k], group: "indicator" })),
  ];
  const hiddenCount = hiddenCols.size;
  const toggleCol  = (key) => setHiddenCols((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const showAllCols = () => setHiddenCols(new Set());

  /* ── filtering logic ── */
  const isTextFilterActive = (key) => !!(textFilters[key] && textFilters[key].trim());
  const isNumFilterActive  = (key) => { const f = numFilters[key]; return !!(f && f.value !== "" && f.value !== undefined); };
  const isFilterActive     = (key) => FIXED_COLS.find((c) => c.key === key) ? isTextFilterActive(key) : isNumFilterActive(key);

  const filteredRows = useMemo(() => rows.filter((row) => {
    for (const fc of FIXED_COLS) {
      const v = textFilters[fc.key];
      if (v && v.trim()) {
        const cell = String(row[fc.key] ?? "").toLowerCase();
        if (!cell.includes(v.trim().toLowerCase())) return false;
      }
    }
    for (const col of cols) {
      const f = numFilters[col.key];
      if (isNumFilterActive(col.key)) {
        if (!applyNumFilter(row[col.key], f)) return false;
      }
    }
    return true;
  }), [rows, textFilters, numFilters, cols]);

  const getSortColumnLabel = useCallback((key) => {
    const fixed = FIXED_COLS.find((c) => c.key === key);
    if (fixed) return fixed.label;
    const metric = cols.find((c) => c.key === key);
    if (metric) return metric.label.replace(/\n/g, " ");
    if (IND_LABELS[key]) return IND_LABELS[key];
    return key;
  }, [cols]);

  const getSortValue = useCallback((row, key) => {
    const fixed = FIXED_COLS.find((c) => c.key === key);
    if (fixed) return row[key] ?? "";
    if (INDICATORS.includes(key)) return row[key] ?? "";
    return row[key] ?? 0;
  }, []);

  const handleSort = useCallback((colKey) => {
    setSortKeys((prev) => toggleSortKey(prev, colKey));
  }, []);

  const onThClick = useMemo(() => createSortThClick(handleSort), [handleSort]);

  const sortedRows = useMemo(() => {
    if (sortKeys.length === 0) return filteredRows;
    return [...filteredRows].sort((a, b) => compareBySortKeys(a, b, sortKeys, getSortValue));
  }, [filteredRows, sortKeys, getSortValue]);

  const hasTableFilters = useMemo(
    () => Object.values(textFilters).some((v) => v && v.trim())
      || Object.entries(numFilters).some(([, f]) => f && f.value !== "" && f.value !== undefined),
    [textFilters, numFilters],
  );

  const displayTotalRow = useMemo(() => {
    if (!totalRow) return null;
    if (!hasTableFilters) return totalRow;
    const out = { ...totalRow };
    for (const col of cols) {
      if (col.isCurrency) {
        out[col.key] = filteredRows.reduce((s, r) => s + (Number(r[col.key]) || 0), 0);
      }
    }
    return out;
  }, [totalRow, hasTableFilters, filteredRows, cols]);

  /* ── pagination ── */
  const totalFiltered = sortedRows.length;
  const totalPages    = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage      = Math.min(page, totalPages);
  const start         = (safePage - 1) * pageSize;
  const pageRows      = sortedRows.slice(start, start + pageSize);
  const pageNumbers   = (() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, totalPages, safePage, safePage - 1, safePage + 1, safePage - 2, safePage + 2]);
    return [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  })();

  /* ── filter panel helpers ── */
  const toggleFilter = useCallback((key) => {
    if (openFilterKey === key) { setOpenFilterKey(null); return; }
    const btn = filterBtnRefs.current[key];
    if (btn) setFilterPanelPos(calcFilterPos(btn));
    setOpenFilterKey(key);
    setOpenOpKey(null);
  }, [openFilterKey]);

  const toggleOpDropdown = useCallback((colKey) => {
    if (openOpKey === colKey) { setOpenOpKey(null); return; }
    const btn = opBtnRefs.current[colKey];
    if (btn) setOpDropdownPos(calcOpDropdownPos(btn));
    setOpenOpKey(colKey);
  }, [openOpKey]);

  const selectOp = (colKey, opKey) => {
    setNumFilters((prev) => ({ ...prev, [colKey]: { ...prev[colKey], op: opKey } }));
    setOpenOpKey(null);
  };

  const updateNumFilter = (colKey, field, value) =>
    setNumFilters((prev) => ({ ...prev, [colKey]: { op: "gt", ...prev[colKey], [field]: value } }));

  const clearFilter = (key) => {
    if (FIXED_COLS.find((c) => c.key === key)) {
      setTextFilters((prev) => { const n = { ...prev }; delete n[key]; return n; });
    } else {
      setNumFilters((prev) => { const n = { ...prev }; delete n[key]; return n; });
    }
  };

  const renderFilterContent = (key) => {
    const isText = !!FIXED_COLS.find((c) => c.key === key);
    if (isText) {
      const val = textFilters[key] || "";
      return (
        <TextFilterContent
          label={getSortColumnLabel(key)}
          value={val}
          onChange={(v) => setTextFilters((p) => ({ ...p, [key]: v }))}
          onClear={() => clearFilter(key)}
        />
      );
    }
    const f = numFilters[key] || { op: "gt", value: "" };
    return (
      <NumericFilterContent
        label={getSortColumnLabel(key)}
        value={f.value ?? ""}
        op={f.op || "gt"}
        onValueChange={(v) => updateNumFilter(key, "value", v)}
        onClear={() => clearFilter(key)}
        onToggleOp={() => toggleOpDropdown(key)}
        opBtnRef={(el) => { opBtnRefs.current[key] = el; }}
      />
    );
  };

  /* ── visible metric + indicator cols ── */
  const visibleCols = useMemo(
    () => cols.filter((c) => !hiddenCols.has(c.key)).map((c) => ({ ...c, w: effectiveMetricWidth(c) })),
    [cols, hiddenCols],
  );
  const visibleInds = INDICATORS.filter((k) => !hiddenCols.has(k));

  const TABLE_W = FIXED_W + visibleCols.reduce((s, c) => s + c.w, 0) + visibleInds.length * IND_W;

  /* ── compute sticky left offsets for visible fixed cols ── */
  let runLeft = 0;
  const visibleFixed = FIXED_COLS.map((c) => {
    const hidden = hiddenCols.has(c.key);
    const col    = { ...c, left: runLeft, hidden };
    if (!hidden) runLeft += c.w;
    return col;
  }).filter((c) => !c.hidden);

  /* Recompute fixed column left offsets for sticky positioning */
  let fl = 0;
  const stickyCols = FIXED_COLS
    .filter((c) => !hiddenCols.has(c.key))
    .map((c) => { const out = { ...c, left: fl }; fl += c.w; return out; });
  const stickyW = fl;

  const effectiveTotalW = stickyW + visibleCols.reduce((s, c) => s + c.w, 0) + visibleInds.length * IND_W;
  const isLastSticky = (idx) => idx === stickyCols.length - 1;

  return (
    <>
      <div style={CARD}>
        {/* ── Head ── */}
        <div style={HEAD}>
          <div>
            <h2 style={TITLE}>{title}</h2>
            {sortKeys.length > 0 && (
              <div style={CARD_META}>
                <span style={{ color: SC_ACCENT, fontWeight: 500 }}>
                  Sorted by {sortKeys.map((s) => getSortColumnLabel(s.key)).join(", ")}
                </span>
              </div>
            )}
          </div>
          <div ref={colPickerRef} style={{ position: "relative", flexShrink: 0 }}>
            <button style={colsBtn(colPickerOpen || hiddenCount > 0)} onClick={() => setColPickerOpen((p) => !p)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="18"/><rect x="14" y="3" width="7" height="18"/>
              </svg>
              Columns
              {hiddenCount > 0 && <span style={hiddenBadge}>{hiddenCount} Hidden</span>}
            </button>
            {colPickerOpen && (
              <div style={colPicker}>
                <div style={colPickerHeader}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", letterSpacing: "0.05em", textTransform: "uppercase" }}>Show / Hide Columns</span>
                  {hiddenCount > 0 && (
                    <button style={{ fontSize: 11, fontWeight: 600, color: "#4F46E5", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: FONT }}
                      onClick={showAllCols}>Show All</button>
                  )}
                </div>
                {hideableCols.map((col) => (
                  <label key={col.key} style={colPickerItem}>
                    <input type="checkbox" style={{ width: 14, height: 14, accentColor: "#4F46E5", cursor: "pointer", flexShrink: 0 }}
                      checked={!hiddenCols.has(col.key)} onChange={() => toggleCol(col.key)} />
                    {col.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={CARD_BODY}>
        {/* ── Table ── */}
        <div style={{ overflowX: "auto" }}>
          <table style={{
            borderCollapse: "collapse", borderSpacing: 0, tableLayout: "fixed",
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif', fontSize: 12,
            width: effectiveTotalW, minWidth: effectiveTotalW,
          }}>
            <colgroup>
              {stickyCols.map((c) => <col key={c.key} style={{ width: c.w }} />)}
              {visibleCols.map((c) => <col key={c.key} style={{ width: c.w }} />)}
              {visibleInds.map((k) => <col key={k} style={{ width: IND_W }} />)}
            </colgroup>
            <thead>
              <tr style={{ height: GH }}>
                {/* Fixed column headers */}
                {stickyCols.map((c, ci) => (
                  <th key={c.key} style={{ ...thFixed(c, isLastSticky(ci)), cursor: "pointer" }} onClick={onThClick(c.key)}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: c.align === "right" ? "flex-end" : "space-between", gap: 3, minWidth: 0 }}>
                      <WaterfallThLabel
                        label={c.label}
                        colKey={c.key}
                        sortKeys={sortKeys}
                        getLabel={getSortColumnLabel}
                        highlight={openFilterKey === c.key}
                        align={c.align}
                      />
                      <button
                        ref={(el) => { filterBtnRefs.current[c.key] = el; }}
                        data-filter-btn
                        style={filterIconBtn(isFilterActive(c.key) || openFilterKey === c.key)}
                        onClick={() => toggleFilter(c.key)}
                        title="Filter"
                      >
                        <FilterFunnelIcon />
                      </button>
                    </div>
                  </th>
                ))}
                {/* Metric column headers */}
                {visibleCols.map((c) => (
                  <th key={c.key} style={{ ...thMetric(c.w), cursor: "pointer" }} onClick={onThClick(c.key)}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, width: "100%", justifyContent: "flex-end" }}>
                      <WaterfallThLabel
                        label={c.label.replace(/\n/g, " ")}
                        colKey={c.key}
                        sortKeys={sortKeys}
                        getLabel={getSortColumnLabel}
                        highlight={openFilterKey === c.key}
                        align="right"
                      />
                      <button
                        ref={(el) => { filterBtnRefs.current[c.key] = el; }}
                        data-filter-btn
                        style={{ ...filterIconBtn(isFilterActive(c.key) || openFilterKey === c.key), flexShrink: 0 }}
                        onClick={() => toggleFilter(c.key)}
                        title="Filter"
                      >
                        <FilterFunnelIcon />
                      </button>
                    </div>
                  </th>
                ))}
                {/* Indicator column headers (no sorting) */}
                {visibleInds.map((k, ki) => (
                  <th key={k} style={thInd(ki === 0)}>
                    {IND_LABELS[k]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Total row — always shown, not paginated */}
              <tr style={{ height: RH }}>
                {stickyCols.map((c, ci) => (
                  <td key={c.key} style={tdTot(c, isLastSticky(ci))}>
                    {ci === 0 ? "Total" : ""}
                  </td>
                ))}
                {visibleCols.map((c) => (
                  <td key={c.key} style={tdTotScroll()}>
                    {formatMetricCell(c, displayTotalRow[c.key])}
                  </td>
                ))}
                {visibleInds.map((k) => (
                  <td key={k} style={tdTotInd()}><StatusDot value={displayTotalRow[k]} /></td>
                ))}
              </tr>
              {/* Data rows — filtered + paginated */}
              {pageRows.map((row, i) => {
                const bg = i % 2 === 0 ? "#FFFFFF" : "#F7FAFC";
                return (
                  <tr key={row.id} style={{ height: RH }}>
                    {stickyCols.map((c, ci) => (
                      <td key={c.key} style={tdFixed(bg, c, isLastSticky(ci))} title={String(row[c.key] ?? "")}>
                        {row[c.key] ?? ""}
                      </td>
                    ))}
                    {visibleCols.map((c) => (
                      <td key={c.key} style={tdBase(bg)}>
                        {formatMetricCell(c, row[c.key])}
                      </td>
                    ))}
                    {visibleInds.map((k) => (
                      <td key={k} style={{ ...tdBase(bg, "center") }}>
                        <StatusDot value={row[k]} />
                      </td>
                    ))}
                  </tr>
                );
              })}
              {pageRows.length === 0 && (
                <tr>
                  <td colSpan={stickyCols.length + visibleCols.length + visibleInds.length}
                    style={{ padding: "24px 20px", textAlign: "center", color: "#9CA3AF", fontSize: 13, fontFamily: FONT }}>
                    No rows match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination footer ── */}
        <div style={paginationBar}>
          <span style={paginationInfo}>
            {totalFiltered === 0
              ? "No rows match the current filters"
              : `${start + 1}–${Math.min(start + pageSize, totalFiltered)} of ${totalFiltered} rows`}
          </span>
          <div style={paginationRight}>
            <span style={pageSizeLabel}>Rows Per Page</span>
            <select style={pageSizeSelect} value={pageSize}
              onChange={(e) => { setPageSize(Number(e.currentTarget.value)); setPage(1); }}>
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <div style={paginationControls}>
              {/* First */}
              <button style={pageBtn(safePage === 1)} disabled={safePage === 1} onClick={() => setPage(1)} title="First">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M9 10L6 7l3-3M5 10V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {/* Prev */}
              <button style={pageBtn(safePage === 1)} disabled={safePage === 1} onClick={() => setPage((p) => p - 1)} title="Previous">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M8.5 10.5L5 7l3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {/* Numbered pages */}
              {pageNumbers.map((p, i) => (
                <React.Fragment key={p}>
                  {i > 0 && pageNumbers[i - 1] < p - 1 && <span style={pageDots}>…</span>}
                  <button
                    style={p === safePage ? pageBtnActive : pageBtn(false)}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                </React.Fragment>
              ))}
              {/* Next */}
              <button style={pageBtn(safePage === totalPages)} disabled={safePage === totalPages} onClick={() => setPage((p) => p + 1)} title="Next">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M5.5 3.5L9 7l-3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {/* Last */}
              <button style={pageBtn(safePage === totalPages)} disabled={safePage === totalPages} onClick={() => setPage(totalPages)} title="Last">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M5 10l3-3-3-3M9 10V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* ── Filter panel portal ── */}
      {openFilterKey && createPortal(
        <div data-filter-panel style={{ ...fpBase, top: filterPanelPos.top, left: filterPanelPos.left }}>
          {renderFilterContent(openFilterKey)}
        </div>,
        document.body
      )}

      {/* ── Operator dropdown portal ── */}
      {openOpKey && createPortal(
        <OpDropdownContent
          activeOp={(numFilters[openOpKey]?.op) || "gt"}
          onSelect={(op) => selectOp(openOpKey, op)}
          style={{ top: opDropdownPos.top, left: opDropdownPos.left }}
        />,
        document.body
      )}
    </>
  );
}
